import { useEffect, useRef } from 'react'
import {
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  Stack,
  Text,
  Icon,
  Separator
} from '@fluentui/react'

interface HelpDialogProps {
  isOpen: boolean
  onDismiss: () => void
}

const HelpDialog = ({ isOpen, onDismiss }: HelpDialogProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  
  const dialogContentProps = {
    type: DialogType.largeHeader,
    title: 'NT CDN User Guide',
    subText: 'Learn how to use the file management and image resizing features'
  }

  useEffect(() => {
    if (isOpen && scrollRef.current) {
      // Small delay to ensure the dialog is fully rendered
      setTimeout(() => {
        scrollRef.current?.scrollTo(0, 0)
      }, 100)
    }
  }, [isOpen])

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={onDismiss}
      dialogContentProps={dialogContentProps}
      modalProps={{
        isBlocking: false,
        styles: { 
          main: { 
            maxWidth: '70vw !important', 
            width: '70vw !important', 
            minWidth: '70vw !important',
            maxHeight: '85vh !important',
            height: 'auto !important'
          }
        }
      }}
    >
      <div 
        ref={scrollRef}
        style={{ 
          maxHeight: '60vh', 
          overflowY: 'auto', 
          paddingRight: '8px' 
        }}
      >
        <Stack tokens={{ childrenGap: 20 }}>
          {/* File Navigation */}
        <Stack tokens={{ childrenGap: 10 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Icon iconName="FolderHorizontal" styles={{ root: { color: '#0078d4' } }} />
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              File Navigation
            </Text>
          </Stack>
          <Text variant="medium">
            • Click on folder names to navigate into directories
          </Text>
          <Text variant="medium">
            • Use the breadcrumb navigation at the top to go back to parent folders
          </Text>
          <Text variant="medium">
            • Use the search box to find files and folders across the entire CDN
          </Text>
          <Text variant="medium">
            • Search results show files from all directories with their full paths
          </Text>
        </Stack>

        <Separator />

        {/* Image Thumbnails */}
        <Stack tokens={{ childrenGap: 10 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Icon iconName="FileImage" styles={{ root: { color: '#0078d4' } }} />
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              Image Thumbnails
            </Text>
          </Stack>
          <Text variant="medium">
            • Image files automatically display small thumbnail previews instead of generic file icons
          </Text>
          <Text variant="medium">
            • Supported formats: JPG, JPEG, PNG, GIF, BMP, WebP, SVG
          </Text>
          <Text variant="medium">
            • Click on an image file to open it in the preview panel
          </Text>
        </Stack>

        <Separator />

        {/* Copy URL Features */}
        <Stack tokens={{ childrenGap: 10 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Icon iconName="Link" styles={{ root: { color: '#0078d4' } }} />
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              Copy File URLs
            </Text>
          </Stack>
          <Text variant="medium">
            <strong>For All Files:</strong>
          </Text>
          <Text variant="medium">
            • Click the "Copy Original URL" button (📋) next to any file to copy its direct link
          </Text>
          <Text variant="medium">
            • The URL will be copied to your clipboard automatically
          </Text>
          <Text variant="medium">
            • A confirmation message will appear when the URL is successfully copied
          </Text>
          
          <Text variant="medium" styles={{ root: { marginTop: 15 } }}>
            <strong>For Image Files:</strong>
          </Text>
          <Text variant="medium">
            • Click the "Copy Resized URL" button (🖼️) to create a custom-sized image link
          </Text>
          <Text variant="medium">
            • A dialog will ask you to specify the desired width and height
          </Text>
          <Text variant="medium">
            • Enter dimensions in pixels (e.g., 800 for width, 600 for height)
          </Text>
          <Text variant="medium">
            • Leave a dimension empty or set to 0 to maintain aspect ratio
          </Text>
          <Text variant="medium">
            • The resized image URL will be automatically copied to your clipboard
          </Text>
        </Stack>

        <Separator />

        {/* Image Resizing Examples */}
        <Stack tokens={{ childrenGap: 10 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Icon iconName="Resize" styles={{ root: { color: '#0078d4' } }} />
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              Image Resizing Examples
            </Text>
          </Stack>
          <Text variant="medium">
            <strong>Fixed width, maintain aspect ratio:</strong> Width: 800, Height: (leave empty)
          </Text>
          <Text variant="medium">
            <strong>Fixed height, maintain aspect ratio:</strong> Width: (leave empty), Height: 600
          </Text>
          <Text variant="medium">
            <strong>Exact dimensions:</strong> Width: 800, Height: 600
          </Text>
          <Text variant="medium">
            <strong>Square thumbnail:</strong> Width: 150, Height: 150
          </Text>
        </Stack>

        <Separator />

        {/* Tips */}
        <Stack tokens={{ childrenGap: 10 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Icon iconName="Lightbulb" styles={{ root: { color: '#0078d4' } }} />
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              Tips & Best Practices
            </Text>
          </Stack>
          <Text variant="medium">
            • Resized images are generated on-demand and cached for better performance
          </Text>
          <Text variant="medium">
            • Smaller images load faster and use less bandwidth
          </Text>
          <Text variant="medium">
            • Use appropriate dimensions for your use case (web pages, thumbnails, etc.)
          </Text>
          <Text variant="medium">
            • All copied URLs are ready to use in web pages, documents, or applications
          </Text>
          <Text variant="medium">
            • Image quality is automatically optimized while maintaining visual fidelity
          </Text>
        </Stack>

        <Separator />

        {/* File Formats */}
        <Stack tokens={{ childrenGap: 10 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Icon iconName="FileTemplate" styles={{ root: { color: '#0078d4' } }} />
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              Supported File Types
            </Text>
          </Stack>
          <Text variant="medium">
            <strong>Images (with resize support):</strong> JPG, JPEG, PNG, GIF, BMP, WebP, SVG
          </Text>
          <Text variant="medium" styles={{ root: { color: '#a80000', fontWeight: 600 } }}>
            ⚠️ This CDN only accepts image files. Other file types are not permitted.
          </Text>
        </Stack>
        </Stack>
      </div>

      <DialogFooter>
        <PrimaryButton onClick={onDismiss} text="Got it!" />
      </DialogFooter>
    </Dialog>
  )
}

export default HelpDialog